import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Component: Product gallery
 * ------------------------------------------------------------------------------
 * An Product gallery file that contains scripts to function.
 * Zoom functionality is using Drift.
 * - https://github.com/imgix/drift#basic-usage
 *
 * @namespace productGallery
 */

import Drift from 'drift-zoom';
import breakpoints from '~/helpers/breakpoints';
import cssClasses from '~/helpers/cssClasses';
import { debounce, on } from '~/helpers/utils';
import Carousel from '~/components/carousel';
/**
 * DOM selectors.
 */

const selectors = {
  container: '[js-product-gallery="container"]',
  featuredImage: '[js-product-gallery="featuredImage"]',
  featuredCarousel: '[js-product-gallery="featuredCarousel"]',
  thumbnail: '[js-product-gallery="thumbnail"]',
  thumbnailCarousel: '[js-product-gallery="thumbnailCarousel"]',
  zoomPane: '[js-product-gallery="zoomPane"]',
  siteHeader: '[js-product-gallery="siteHeader"]',
  productAside: '[js-product-gallery="productAside"]'
};
/**
 * Attribute and data selectors.
 */

const dataSelectors = {
  thumbnail: 'js-product-gallery'
};
/**
 * Export a new product gallery.
 */

export default (() => {
  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: [...document.querySelectorAll(selectors.container)],
    featuredImage: [...document.querySelectorAll(selectors.featuredImage)],
    featuredCarousel: document.querySelector(selectors.featuredCarousel),
    thumbnail: [...document.querySelectorAll(selectors.thumbnail)],
    thumbnailCarousel: document.querySelector(selectors.thumbnailCarousel),
    zoomPane: document.querySelector(selectors.zoomPane),
    siteHeader: document.querySelector(selectors.siteHeader),
    productAside: document.querySelector(selectors.productAside)
  };
  /**
   * Instance globals.
   */

  const namespace = nodeSelectors.featuredCarousel;
  const featuredImages = {};
  let featuredCarousel = {};
  let thumbnailCarousel = {};
  /**
   * Options for Drift zoom.
   */

  const zoomOptions = {
    hoverBoundingBox: true,
    touchBoundingBox: true,
    paneContainer: nodeSelectors.zoomPane,
    touchDelay: 500,
    zoomFactor: 2
  };
  /**
   * Initialise component.
   */

  function init() {
    if (!nodeSelectors.thumbnailCarousel) {
      return;
    }

    setDefaultState();
    setEventBusListeners();
    setEventListeners();
  }
  /**
   * Set event listeners.
   */


  function setEventListeners() {
    on('resize', debounce(handleResizeEvent, 250));
  }
  /**
   * Updated height of gallery images based on site header & product form on mobile.
   */


  function updateGalleryImagesHeight() {
    const siteHeaderBound = nodeSelectors.siteHeader.getBoundingClientRect();
    const productAsideBound = nodeSelectors.productAside.getBoundingClientRect();
    const sideHeaderHeight = siteHeaderBound.height;
    const productAsideHeight = productAsideBound.height;
    [...document.querySelectorAll(selectors.featuredImage)].forEach(image => {
      if (window.matchMedia("(max-width: ".concat(breakpoints.large, ")")).matches) {
        image.style.height = "calc(100vh - ".concat(sideHeaderHeight + productAsideHeight, "px)");
      } else {
        image.style.height = "calc(100vh - ".concat(sideHeaderHeight, "px)");
      }
    });
  }
  /**
   * Listen for product form change events.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen('ProductForm:change', _ref => {
      let {
        currentVariant
      } = _ref;

      if (!isVariantImageAvailable(currentVariant.id)) {
        return;
      }

      const selectedVariantImage = document.querySelector("".concat(selectors.thumbnail, "[data-variant-id=\"").concat(currentVariant.id, "\""));
      const id = selectedVariantImage.dataset.id;
      const selectedThumbnail = document.querySelector("".concat(selectors.thumbnail, "[data-id=\"").concat(id, "\"]"));
      updateSelectedState(selectedThumbnail);
      featuredCarousel.select(id);
    });
    Frame.EventBus.listen(["Carousel-".concat(namespace.id, ":ready"), "Carousel-".concat(namespace.id, ":resize")], () => {
      updateGalleryImagesHeight();
    });
    Frame.EventBus.listen("Carousel-".concat(namespace.id, ":settle"), _ref2 => {
      let {
        selectedIndex
      } = _ref2;
      const selectedThumbnail = document.querySelector("".concat(selectors.thumbnail, "[data-id=\"").concat(selectedIndex, "\"]"));
      updateSelectedState(selectedThumbnail);
      updateThumbnailCarousel(selectedIndex);
    });
  }
  /**
   * Construct carousel objects on gallery images.
   */


  function constructCarousels() {
    thumbnailCarousel = Carousel(selectors.thumbnailCarousel, {
      swiper: {
        freeMode: true,
        keyboard: false,
        loop: true,
        loopedSlides: 4,
        slidesPerView: 4,
        spaceBetween: 0,
        watchSlidesProgress: true,
        watchSlidesVisibility: true
      }
    });
    featuredCarousel = Carousel(selectors.featuredCarousel, {
      swiper: {
        lazy: {
          loadPrevNext: true,
          loadPrevNextAmount: 2
        },
        loop: true,
        loopedSlides: 4,
        spaceBetween: 0,
        navigation: {
          nextEl: '.product-gallery__button-next',
          prevEl: '.product-gallery__button-prev'
        },
        pagination: {
          el: '.product-gallery__pagination',
          type: 'fraction'
        }
      }
    });
  }
  /**
   * Set default state based on screen size.
   */


  function setDefaultState() {
    constructCarousels();
    initCarousels();
    setThumbnailClickEvents();
  }
  /**
   * Initialise gallery and thumbnail carousels
   */


  function initCarousels() {
    featuredCarousel.init();
  }
  /**
   * Handle resize events through debounce callback.
   * - Disable zoom on small devices.
   * - Remove carousel on thumbnails on large screens.
   */


  function handleResizeEvent() {
    featuredCarousel.update();
    setThumbnailClickEvents();

    if (!window.matchMedia("(min-width: ".concat(breakpoints.large, ")")).matches) {
      if (thumbnailCarousel.isInitialised()) {
        thumbnailCarousel.update();
      } else {
        thumbnailCarousel.init();
      }

      if (theme.features.imageZoom) {
        destroyZoomEvents();
      }

      return;
    }

    thumbnailCarousel.destroy();

    if (theme.features.imageZoom) {
      setZoomEvents();
    }
  }
  /**
   * Set thumbnail event handlers to update on click.
   */


  function setThumbnailClickEvents() {
    on('click', nodeSelectors.thumbnailCarousel, handleThumbnailClick);
  }
  /**
   * Handle thumbnail image click.
   * @param {Event} event - Click event on image thumbnail.
   */


  function handleThumbnailClick(event) {
    event.preventDefault();
    const target = event.target.closest(selectors.thumbnail);

    if (isTargetThumbnail(target)) {
      updateSelectedState(target);
      updateFeaturedImage(target.dataset.id);
    }
  }
  /**
   * Detect quick view toggle click.
   * @param {HTMLElement} target - Clicked target to check quick view attributes against.
   * @returns {Boolean}
   */


  function isTargetThumbnail(target) {
    return typeof target.attributes[dataSelectors.thumbnail] !== 'undefined' && target.attributes[dataSelectors.thumbnail].nodeValue === 'thumbnail';
  }
  /**
   * Update state of selected thumbnail target.
   * @param {Object} target - Selected thumbnail element.
   */


  function updateSelectedState(target) {
    nodeSelectors.thumbnail.forEach(element => {
      element.classList.remove(cssClasses.active);
    });
    target.classList.add(cssClasses.active);
  }
  /**
   * Updates selected slide on thumbnail slider.
   * @param {Number} index - Slide index to select.
   */


  function updateThumbnailCarousel(id) {
    if (!thumbnailCarousel.isInitialised()) {
      return;
    }

    thumbnailCarousel.select(id);
  }
  /**
   * Switch main thumbnail image.
   * @param {Number} id - Target thumbnail id to switch to.
   */


  function updateFeaturedImage(id) {
    featuredCarousel.select(id);
  }
  /**
   * Check if variant image is available.
   * @param {Number} id - Variant id of selected option.
   * @returns {Boolean}
   */


  function isVariantImageAvailable(id) {
    return Boolean(document.querySelector("".concat(selectors.thumbnail, "[data-variant-id=\"").concat(id, "\"")));
  }
  /**
   * Set zoom events on featured images.
   */


  function setZoomEvents() {
    const featuredImage = [...nodeSelectors.featuredCarousel.querySelectorAll(selectors.featuredImage)];
    featuredImage.forEach((element, index) => {
      featuredImages[index] = new Drift(element.querySelector('img'), zoomOptions);
    });
  }
  /**
   * Remove zoom events on featured images.
   */


  function destroyZoomEvents() {
    Object.keys(featuredImages).forEach(key => {
      featuredImages[key].destroy();
    });
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init,
    isVariantImageAvailable,
    updateFeaturedImage
  });
});